import React, { useState, useEffect } from 'react';

const VehicleForm = ({ vehicle, customer, onSubmit, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    brand: '',
    model: '',
    year: '',
    license_plate: '',
    vin: '',
    engine: '',
    fuel_type: '',
    mileage: '',
    color: '',
    first_registration: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  const fuelTypes = [
    'Essence',
    'Diesel', 
    'Electrique',
    'Hybride',
    'GPL',
    'GNV'
  ];

  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (vehicle) {
      setFormData({
        brand: vehicle.brand || '',
        model: vehicle.model || '',
        year: vehicle.year || '',
        license_plate: vehicle.license_plate || '',
        vin: vehicle.vin || '',
        engine: vehicle.engine || '',
        fuel_type: vehicle.fuel_type || '',
        mileage: vehicle.mileage || '',
        color: vehicle.color || '',
        first_registration: vehicle.first_registration || '',
        notes: vehicle.notes || ''
      });
    }
  }, [vehicle]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for the changed field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.brand.trim()) {
      newErrors.brand = 'La marque est requise';
    }

    if (formData.year && (formData.year < 1900 || formData.year > currentYear + 1)) {
      newErrors.year = `L'année doit être entre 1900 et ${currentYear + 1}`;
    }

    if (formData.license_plate && !/^[A-Z0-9\-\s]+$/i.test(formData.license_plate)) {
      newErrors.license_plate = 'Format de plaque d\'immatriculation invalide';
    }

    if (formData.vin && formData.vin.length !== 17) {
      newErrors.vin = 'Le VIN doit contenir exactement 17 caractères';
    }

    if (formData.mileage && (isNaN(formData.mileage) || formData.mileage < 0)) {
      newErrors.mileage = 'Le kilométrage doit être un nombre positif';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitData = {
        ...formData,
        customer_id: customer.id,
        year: formData.year ? parseInt(formData.year) : null,
        mileage: formData.mileage ? parseInt(formData.mileage) : null
      };
      onSubmit(submitData);
    }
  };

  return (
    <div className="vehicle-form-overlay">
      <div className="vehicle-form-container">
        <div className="vehicle-form-header">
          <h2>
            {vehicle ? 'Modifier le véhicule' : 'Nouveau véhicule'}
            {customer && <span className="customer-info">pour {customer.name}</span>}
          </h2>
          <button className="close-btn" onClick={onCancel}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="vehicle-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="brand">Marque *</label>
              <input
                type="text"
                id="brand"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                placeholder="Renault, Peugeot, Citroën..."
                className={errors.brand ? 'error' : ''}
              />
              {errors.brand && <span className="error-text">{errors.brand}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="model">Modèle</label>
              <input
                type="text"
                id="model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                placeholder="Clio, 308, C3..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="year">Année</label>
              <input
                type="number"
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                placeholder="2020"
                min="1900"
                max={currentYear + 1}
                className={errors.year ? 'error' : ''}
              />
              {errors.year && <span className="error-text">{errors.year}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="fuel_type">Carburant</label>
              <select
                id="fuel_type"
                name="fuel_type"
                value={formData.fuel_type}
                onChange={handleChange}
              >
                <option value="">Sélectionner...</option>
                {fuelTypes.map(fuel => (
                  <option key={fuel} value={fuel}>{fuel}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="license_plate">Plaque d'immatriculation</label>
              <input
                type="text"
                id="license_plate"
                name="license_plate"
                value={formData.license_plate}
                onChange={handleChange}
                placeholder="AB-123-CD"
                className={errors.license_plate ? 'error' : ''}
              />
              {errors.license_plate && <span className="error-text">{errors.license_plate}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="color">Couleur</label>
              <input
                type="text"
                id="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                placeholder="Blanc, Noir, Rouge..."
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="mileage">Kilométrage</label>
              <input
                type="number"
                id="mileage"
                name="mileage"
                value={formData.mileage}
                onChange={handleChange}
                placeholder="85000"
                min="0"
                className={errors.mileage ? 'error' : ''}
              />
              {errors.mileage && <span className="error-text">{errors.mileage}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="first_registration">Première immatriculation</label>
              <input
                type="date"
                id="first_registration"
                name="first_registration"
                value={formData.first_registration}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="engine">Motorisation</label>
              <input
                type="text"
                id="engine"
                name="engine"
                value={formData.engine}
                onChange={handleChange}
                placeholder="1.2 TCe, 1.6 HDi, 2.0 TDI..."
              />
            </div>

            <div className="form-group">
              <label htmlFor="vin">Numéro VIN</label>
              <input
                type="text"
                id="vin"
                name="vin"
                value={formData.vin}
                onChange={handleChange}
                placeholder="VF1RH123456789012"
                maxLength="17"
                className={errors.vin ? 'error' : ''}
              />
              {errors.vin && <span className="error-text">{errors.vin}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Informations complémentaires sur le véhicule..."
              rows="4"
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel">
              Annuler
            </button>
            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? 'Enregistrement...' : (vehicle ? 'Mettre à jour' : 'Créer le véhicule')}
            </button>
          </div>
        </form>

        <style jsx>{`
          .vehicle-form-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .vehicle-form-container {
            background: white;
            border-radius: 12px;
            width: 100%;
            max-width: 800px;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .vehicle-form-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 24px 24px 0 24px;
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 24px;
          }

          .vehicle-form-header h2 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
            color: #1f2937;
          }

          .customer-info {
            font-size: 1rem;
            font-weight: 400;
            color: #6b7280;
            margin-left: 8px;
          }

          .close-btn {
            background: none;
            border: none;
            color: #6b7280;
            cursor: pointer;
            padding: 8px;
            border-radius: 6px;
            transition: all 0.2s ease;
          }

          .close-btn:hover {
            background: #f3f4f6;
            color: #374151;
          }

          .vehicle-form {
            padding: 0 24px 24px 24px;
          }

          .form-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
            margin-bottom: 16px;
          }

          @media (max-width: 640px) {
            .form-row {
              grid-template-columns: 1fr;
            }
          }

          .form-group {
            position: relative;
            margin-bottom: 16px;
          }

          .form-group label {
            display: block;
            margin-bottom: 6px;
            font-weight: 500;
            color: #374151;
            font-size: 0.9rem;
          }

          .form-group input,
          .form-group select,
          .form-group textarea {
            width: 100%;
            padding: 12px 16px;
            border: 1px solid #d1d5db;
            border-radius: 8px;
            font-size: 0.9rem;
            transition: all 0.2s ease;
            box-sizing: border-box;
          }

          .form-group input:focus,
          .form-group select:focus,
          .form-group textarea:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .form-group input.error,
          .form-group select.error,
          .form-group textarea.error {
            border-color: #dc2626;
          }

          .form-group textarea {
            resize: vertical;
            min-height: 100px;
          }

          .error-text {
            color: #dc2626;
            font-size: 0.8rem;
            margin-top: 4px;
            display: block;
          }

          .form-actions {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 32px;
            padding-top: 24px;
            border-top: 1px solid #e5e7eb;
          }

          .btn-cancel,
          .btn-submit {
            padding: 12px 24px;
            border-radius: 8px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            border: none;
            font-size: 0.9rem;
          }

          .btn-cancel {
            background: #f3f4f6;
            color: #374151;
          }

          .btn-cancel:hover {
            background: #e5e7eb;
          }

          .btn-submit {
            background: #3b82f6;
            color: white;
          }

          .btn-submit:hover:not(:disabled) {
            background: #2563eb;
          }

          .btn-submit:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}</style>
      </div>
    </div>
  );
};

export default VehicleForm;
